'use client';

import { init, type RematchDispatch, type RematchRootState } from '@rematch/core';
import immerPlugin from '@rematch/immer';
import loadingPlugin, { type ExtraModelsFromLoading } from '@rematch/loading';
import selectPlugin from '@rematch/select';

// The integration-auth and persisted-preferences state both left this blob for
// their own per-key storage, and their migrations read the serialized blob
// directly. Importing them here — above `init` — is what guarantees they run
// before redux-persist's first write, which rewrites `persist:root` from the
// whitelist and would otherwise drop the entries they read. Goes when this file
// does.
import '@/atoms/integrationAuthAtoms';
import '@/atoms/persistedPreferencesAtoms';
import { attachAuthRevisionTracking } from '@/lib/auth/authRevisionTracking';
import { logger } from '@/utils/logger/logger';
import { attachEhrLaunchRegionIdentityTracking } from '@heidi/integrations/ehr/data/atoms';
import persistPlugin from '@rematch/persist';
import { createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { models, type RootModel } from './models';
import type { InitialState as AuthState } from './models/authModel';
import type { InitialState as ForceUpdateState } from './models/forceUpdateModel';
import type { InitialState as TauriUpdateState } from './models/tauriUpdateModel';

/** IF YOU USE THE BASIC SETUP USE THIS METHOD * */
type FullModel = ExtraModelsFromLoading<RootModel>;

// Transform to exclude transient fields from persistence
const forceUpdateTransform = createTransform<ForceUpdateState, ForceUpdateState>(
  // Transform state on its way to being serialized and stored
  (inboundState) => {
    // Exclude transient runtime state from being persisted
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isInstalling, checkIntervalId, ...rest } = inboundState;
    return { ...rest, isInstalling: false, checkIntervalId: null };
  },
  // Transform state being rehydrated
  (outboundState) => {
    // Always initialize transient fields when rehydrating
    return {
      ...outboundState,
      isInstalling: false,
      checkIntervalId: null,
    };
  },
  // Only apply to forceUpdateModel
  { whitelist: ['forceUpdateModel'] }
);

// `auth.userRegion` is retired from storage, not migrated: it is re-derivable
// from the profile region, the invitation region, the token (which rehydrates
// in this same slice) and finally the hostname, and `getRegion()` fails open
// to Region.AU. Dropped in BOTH directions so the field is never written again
// and a legacy blob's copy is never rehydrated; the in-session value written by
// `setUserRegion` is unaffected.
const authTransform = createTransform<AuthState, AuthState>(
  (inboundState) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { userRegion, ...rest } = inboundState;
    return rest;
  },
  (outboundState) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { userRegion, ...rest } = outboundState;
    return rest;
  },
  // Only apply to the auth model
  { whitelist: ['auth'] }
);

// `rejected_update` is session-scoped: a SIGNATURE_INVALID from a truncated
// download must not suppress that release after a restart.
const tauriUpdateTransform = createTransform<TauriUpdateState, TauriUpdateState>(
  (inboundState) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { rejected_update, ...rest } = inboundState;
    return rest;
  },
  (outboundState) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { rejected_update, ...rest } = outboundState;
    return rest;
  },
  { whitelist: ['tauriUpdateModel'] }
);

const persistConfig = {
  version: 1,
  key: 'root',
  storage,
  whitelist: ['auth', 'tauriUpdateModel', 'forceUpdateModel'],
  transforms: [forceUpdateTransform, authTransform, tauriUpdateTransform],
  // redux-persist swallows write failures internally (createPersistoid.js wraps
  // writes in .catch(onWriteFail)), so getPersistor().flush() always resolves —
  // the try/catch blocks in FHIR launch hooks cannot observe write failures.
  // Log here so failures are at least visible in structured logs.
  writeFailHandler: (err: unknown) => {
    logger.warn('[persist] localStorage write failed', {
      error: err instanceof Error ? err.message : String(err),
    });
  },
};

export const store = init<RootModel, FullModel>({
  models,
  plugins: [loadingPlugin(), selectPlugin(), immerPlugin(), persistPlugin(persistConfig)],
});

// The auth revision register lives outside this store (`@heidi/auth/state`)
// and is advanced by an observer of the `(token, authProvider)` pair rather
// than by the reducers that write it — including redux-persist's own
// rehydrate merge, which bypasses every reducer. This is the one place the
// writer is wired.
attachAuthRevisionTracking(store);

// Same mechanism, same reason, for the persisted EHR-launch region: it replaced
// `auth.userRegion`, which was self-correcting because eight `auth.clear()`
// sites wiped it and the next `setUserRegion` overwrote it. The carrier sits
// ABOVE `userRegion` in the precedence, so instead of a teardown obligation on
// every one of those paths forever, its validity is keyed to the token's `sub`
// and enforced here by one observer of the slice.
attachEhrLaunchRegionIdentityTracking(store);

export type Store = typeof store;
export type Dispatch = RematchDispatch<RootModel>;
export type RootState = RematchRootState<RootModel, FullModel>;
